import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

export const Reveal = ({ children, delay = 0, y = 26, className = "", once = true }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once, amount: 0.15 }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export function CountUp({ to, suffix = "", prefix = "", duration = 1.8, decimals = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / (duration * 1000));
      const e = 1 - Math.pow(1 - p, 3);
      setVal(to * e);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);
  return (
    <span ref={ref} className={className}>
      {prefix}
      {val.toLocaleString("en-IN", { maximumFractionDigits: decimals, minimumFractionDigits: decimals })}
      {suffix}
    </span>
  );
}

export function SectionHead({ eyebrow, title, sub, align = "left", light = false }) {
  const alignCls = align === "center" ? "items-center text-center" : "items-start text-left";
  return (
    <div className={`flex flex-col gap-4 ${alignCls}`}>
      {eyebrow && (
        <Reveal>
          <span className={`eyebrow ${light ? "eyebrow-dark" : ""}`}>{eyebrow}</span>
        </Reveal>
      )}
      <Reveal delay={0.06}>
        <h2 className={`max-w-3xl text-3xl font-bold leading-[1.12] tracking-tight sm:text-4xl lg:text-5xl ${light ? "text-nighttext" : "text-paper"}`}>
          {title}
        </h2>
      </Reveal>
      {sub && (
        <Reveal delay={0.12}>
          <p className={`max-w-2xl text-base leading-relaxed sm:text-lg ${light ? "text-[#5c5470]" : "text-mist"}`}>{sub}</p>
        </Reveal>
      )}
    </div>
  );
}

export function Btn({ variant = "primary", size = "md", as: As = "button", className = "", children, ...props }) {
  const sizes = {
    md: "px-6 py-3",
    sm: "px-4 py-2 text-[13px]",
    lg: "px-7 py-3.5 text-[15px]",
  };
  const variants = {
    primary: "btn-primary",
    outline: "btn-outline",
    outlineDark: "btn-outline-dark",
    ghost: "btn-ghost",
  };
  return (
    <As className={`btn ${variants[variant]} ${sizes[size]} ${className}`} {...props}>
      {children}
    </As>
  );
}

export function Field({ label, error, children, required = true, dark = false }) {
  return (
    <label className="flex w-full flex-col gap-1.5 text-left">
      <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${dark ? "text-[#6F6890]" : "text-mist-2"}`}>
        {label} {required && <span className="text-carza">*</span>}
      </span>
      {children}
      {error && <span className="text-xs text-red-500">{error}</span>}
    </label>
  );
}

export function StatusPill({ status }) {
  const map = {
    new: "bg-carza-dim text-carza-lite border-carza/30",
    contacted: "bg-amber-400/10 text-amber-300 border-amber-400/30",
    converted: "bg-emerald-400/10 text-emerald-300 border-emerald-400/30",
    closed: "bg-white/5 text-mist-2 border-white/10",
  };
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest ${map[status] || map.closed}`}>
      {status}
    </span>
  );
}
